import {
  type NotificationBase,
  type NotificationFromQuery,
} from '@/store/notifications/notifications'

import { type Props as PropsNotificationExternalServiceRequestCreated } from '@/components/PmNotification/PmNotificationExternalServiceRequestCreated/PmNotificationExternalServiceRequestCreated.vue'
import { type Props as PropsNotificationExternalServiceRequestUpdated } from '@/components/PmNotification/PmNotificationExternalServiceRequestUpdated/PmNotificationExternalServiceRequestUpdated.vue'
import { type Props as PropsNotificationExternalServiceRequestAccepted } from '@/components/PmNotification/PmNotificationExternalServiceRequestAccepted/PmNotificationExternalServiceRequestAccepted.vue'
import { type Props as PropsNotificationExternalServiceRequestDeclined } from '@/components/PmNotification/PmNotificationExternalServiceRequestDeclined/PmNotificationExternalServiceRequestDeclined.vue'
import { type Props as PropsNotificationExternalServiceRequestAcceptedNotice } from '@/components/PmNotification/PmNotificationExternalServiceRequestAcceptedNotice/PmNotificationExternalServiceRequestAcceptedNotice.vue'
import { type Props as PropsNotificationExternalServiceRequestDeclinedNotice } from '@/components/PmNotification/PmNotificationExternalServiceRequestDeclinedNotice/PmNotificationExternalServiceRequestDeclinedNotice.vue'

import { parseServerDateString } from '@/utilities/date'
import { getDisplayNameOfAddress } from '@/utilities/string'

export interface NotificationExternalServiceRequestCreated {
  type: 'NotificationExternalServiceRequestCreated'
  props: Omit<
    PropsNotificationExternalServiceRequestCreated,
    keyof NotificationBase | 'isJumpVisible'
  >
}

export function normalizeNotificationExternalServiceRequestCreated(
  notification: NotificationFromQuery
) {
  if (notification.meta?.__typename !== 'ExternalServiceRequestCreated') {
    throw new Error(
      'notification.meta.__typename is not ExternalServiceRequestCreated'
    )
  }
  const details = getExternalServiceRequestDetails(notification)

  const result: NotificationExternalServiceRequestCreated = {
    type: 'NotificationExternalServiceRequestCreated',
    props: {
      externalServiceRequestId: details.id,
      name: getDisplayNameOfAddress(notification.meta.user),
      startDate: parseServerDateString(notification.meta.startDate, {
        type: 'date',
      }),
      endDate: parseServerDateString(notification.meta.endDate, {
        type: 'date',
      }),
    },
  }

  return result
}

export interface NotificationExternalServiceRequestUpdated {
  type: 'NotificationExternalServiceRequestUpdated'
  props: Omit<
    PropsNotificationExternalServiceRequestUpdated,
    keyof NotificationBase | 'isJumpVisible'
  >
}

export function normalizeNotificationExternalServiceRequestUpdated(
  notification: NotificationFromQuery
) {
  const details = getExternalServiceRequestDetails(notification)
  const shared = normalizeSharedExternalServiceRequestDetails(notification)

  const result: NotificationExternalServiceRequestUpdated = {
    type: 'NotificationExternalServiceRequestUpdated',
    props: {
      ...shared,
      externalServiceRequestOfUserId: details.user.id,
    },
  }

  return result
}

export interface NotificationExternalServiceRequestAccepted {
  type: 'NotificationExternalServiceRequestAccepted'
  props: Omit<
    PropsNotificationExternalServiceRequestAccepted,
    keyof NotificationBase
  >
}

export function normalizeNotificationExternalServiceRequestAccepted(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedExternalServiceRequestDetails(notification)

  const result: NotificationExternalServiceRequestAccepted = {
    type: 'NotificationExternalServiceRequestAccepted',
    props: {
      externalServiceRequestId: shared.externalServiceRequestId,
      startDate: shared.startDate,
      endDate: shared.endDate,
    },
  }

  return result
}

export interface NotificationExternalServiceRequestAcceptedNotice {
  type: 'NotificationExternalServiceRequestAcceptedNotice'
  props: Omit<
    PropsNotificationExternalServiceRequestAcceptedNotice,
    keyof NotificationBase
  >
}

export function normalizeNotificationExternalServiceRequestAcceptedNotice(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedExternalServiceRequestDetails(notification)

  const result: NotificationExternalServiceRequestAcceptedNotice = {
    type: 'NotificationExternalServiceRequestAcceptedNotice',
    props: shared,
  }

  return result
}

export interface NotificationExternalServiceRequestDeclined {
  type: 'NotificationExternalServiceRequestDeclined'
  props: Omit<
    PropsNotificationExternalServiceRequestDeclined,
    keyof NotificationBase
  >
}

export function normalizeNotificationExternalServiceRequestDeclined(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedExternalServiceRequestDetails(notification)

  const result: NotificationExternalServiceRequestDeclined = {
    type: 'NotificationExternalServiceRequestDeclined',
    props: {
      externalServiceRequestId: shared.externalServiceRequestId,
      startDate: shared.startDate,
      endDate: shared.endDate,
    },
  }

  return result
}

export interface NotificationExternalServiceRequestDeclinedNotice {
  type: 'NotificationExternalServiceRequestDeclinedNotice'
  props: Omit<
    PropsNotificationExternalServiceRequestDeclinedNotice,
    keyof NotificationBase
  >
}

export function normalizeNotificationExternalServiceRequestDeclinedNotice(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedExternalServiceRequestDetails(notification)

  const result: NotificationExternalServiceRequestDeclinedNotice = {
    type: 'NotificationExternalServiceRequestDeclinedNotice',
    props: shared,
  }

  return result
}

function normalizeSharedExternalServiceRequestDetails(
  notification: NotificationFromQuery
) {
  const details = getExternalServiceRequestDetails(notification)

  return {
    externalServiceRequestId: details.id,
    name: getDisplayNameOfAddress(details.user),
    startDate: parseServerDateString(details.startDate),
    endDate: parseServerDateString(details.endDate),
  }
}

// TODO: Should this be an assertion instead?
function getExternalServiceRequestDetails(notification: NotificationFromQuery) {
  if (notification.details?.__typename !== 'ExternalServiceRequest') {
    throw new Error(
      'notification.details.__typename is not ExternalServiceRequest'
    )
  }

  return notification.details
}
